/**
 * Cloudflare Turnstile widget for the last signup step.
 *
 * The wizard mounts the widget when the last step opens. The mount loads
 * api.js with `render=explicit` and renders the widget, so the check is
 * visible before the user clicks Create Account. Each widget event maps to
 * one state:
 *
 *   off          no sitekey, no widget, or a browser Turnstile does not support
 *   loading      the widget runs its check and has no token yet
 *   interactive  Cloudflare shows a checkbox that the user must tick
 *   ready        a token exists
 *   error        a widget error, an expired challenge, api.js failed to load,
 *                or no answer in time
 *
 * The backend accepts a submit without a token, so no state stops a submit
 * for good.
 */

// Bounds the wait for api.js and for a non-interactive check that never
// calls back. An interactive challenge stops this clock, and Cloudflare's
// own timeout-callback bounds it.
export const TIMEOUT_MS = 15000;

const CONTAINER_SELECTOR = '#signupForm [data-signup-turnstile]';
const API_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let widgetId = null;
let token = '';
let state = 'off';
let timeoutId = null;
let apiRequested = false;
let unsupported = false;
let onStateChange = () => {};
let settleWaiters = [];

function setState(next) {
    state = next;
    onStateChange(next);

    if (next !== 'loading') {
        const waiters = settleWaiters;
        settleWaiters = [];
        waiters.forEach((resolve) => resolve(next));
    }
}

function stopClock() {
    if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
    }
}

function startClock() {
    stopClock();
    timeoutId = setTimeout(() => {
        timeoutId = null;
        if (state === 'loading') {
            setState('error');
        }
    }, TIMEOUT_MS);
}

function findContainer() {
    return document.querySelector(CONTAINER_SELECTOR);
}

function renderWidget() {
    const container = findContainer();
    if (!container || widgetId !== null || typeof window.turnstile === 'undefined') {
        return;
    }

    try {
        widgetId = window.turnstile.render(container, {
            sitekey: container.dataset.sitekey,
            size: 'flexible',
            theme: 'light',
            callback(value) {
                stopClock();
                token = value || '';
                setState(token ? 'ready' : 'error');
            },
            'error-callback'(code) {
                console.warn('[Signup] Turnstile error:', code);
                stopClock();
                token = '';
                setState('error');
                return true;
            },
            // `refresh-expired` defaults to auto, so the widget mints a
            // replacement token itself.
            'expired-callback'() {
                token = '';
                setState('loading');
                startClock();
            },
            'timeout-callback'() {
                stopClock();
                token = '';
                setState('error');
            },
            'before-interactive-callback'() {
                stopClock();
                setState('interactive');
            },
            // The user ticked the checkbox. A click waits for the token.
            'after-interactive-callback'() {
                if (state === 'interactive') {
                    setState('loading');
                    startClock();
                }
            },
            'unsupported-callback'() {
                stopClock();
                unsupported = true;
                token = '';
                setState('off');
            },
        });
    } catch (e) {
        console.warn('[Signup] Turnstile render failed:', e);
        stopClock();
        setState('error');
    }
}

function loadApi() {
    if (apiRequested) {
        return;
    }
    apiRequested = true;

    const script = document.createElement('script');
    script.src = API_URL;
    script.async = true;
    script.onload = () => {
        if (widgetId !== null || state === 'off') {
            return;
        }
        // A load that lands after the clock ran out still gets its check.
        if (state === 'error') {
            setState('loading');
            startClock();
        }
        renderWidget();
    };
    script.onerror = () => {
        apiRequested = false;
        if (state === 'loading') {
            stopClock();
            setState('error');
        }
    };
    document.head.appendChild(script);
}

/**
 * Renders the widget once and reports every state change to `callback`.
 * A later call only replaces the callback, so the widget and its token
 * survive a trip back to an earlier step.
 *
 * @param {(state: string) => void} callback
 */
export function mountTurnstile(callback) {
    onStateChange = callback;

    if (widgetId !== null || state !== 'off' || unsupported || !findContainer()) {
        onStateChange(state);
        return;
    }

    setState('loading');
    startClock();
    if (typeof window.turnstile === 'undefined') {
        loadApi();
        return;
    }
    renderWidget();
}

/**
 * Clears the token and runs a fresh check. A token is single-use, so the
 * next submit after a rejected POST needs a new one. When api.js never
 * loaded, the script loads again.
 */
export function resetTurnstile() {
    if (unsupported || (state === 'off' && widgetId === null)) {
        return;
    }

    token = '';
    setState('loading');
    startClock();

    if (widgetId !== null && typeof window.turnstile !== 'undefined') {
        try {
            window.turnstile.reset(widgetId);
        } catch (e) {
            console.warn('[Signup] Turnstile reset failed:', e);
            stopClock();
            setState('error');
        }
        return;
    }

    if (typeof window.turnstile !== 'undefined') {
        renderWidget();
        return;
    }

    loadApi();
}

/** The token for this submit, or '' when the widget has none. */
export function getTurnstileToken() {
    return token;
}

/**
 * Resolves with the state once the check leaves `loading`. The clock
 * bounds the wait to TIMEOUT_MS.
 *
 * @returns {Promise<string>}
 */
export function whenTurnstileSettles() {
    if (state !== 'loading') {
        return Promise.resolve(state);
    }

    return new Promise((resolve) => {
        settleWaiters.push(resolve);
    });
}
